import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatFilesize } from '#layers/assets/utils/format-filesize';
import { getErrorStatusCode, useSystemInfo } from '#layers/base/public';
import { useErrorFormatter } from '#layers/ui/public';

/**
 * Advertises the server's max file upload size (`config.maxFileUploadSize`,
 * surfaced by `useSystemInfo`) and turns an upload failure into a user-facing
 * message, size-specific when the backend rejects the file for being too large.
 */
export function useFileUploadLimit() {
	const { t } = useI18n();
	const { formatError } = useErrorFormatter();
	const systemInfo = useSystemInfo();

	const maxFileUploadSizeBytes = systemInfo.maxFileUploadSize;

	const maxFileUploadSizeLabel = computed(() => {
		if (maxFileUploadSizeBytes === null) return null;

		return formatFilesize(maxFileUploadSizeBytes);
	});

	function resolveUploadErrorMessage(error: unknown): string {
		// The backend returns 413 when the upload exceeds config.maxFileUploadSize.
		if (getErrorStatusCode(error) === 413 && maxFileUploadSizeLabel.value) {
			return t('upload.file_too_large', { size: maxFileUploadSizeLabel.value });
		}

		return formatError(error);
	}

	return { maxFileUploadSizeBytes, maxFileUploadSizeLabel, resolveUploadErrorMessage };
}
